import type { CaseInputs, SchemaEntry, View } from './types';
import { formulaDependencies } from './formulas';

const GPS_IDENTITY = ['is.total_orders', 'is.avg_ticket'];

function collectFormulaEdges(schema: SchemaEntry[]): Map<string, Set<string>> {
    const edges = new Map<string, Set<string>>();
    for (const entry of schema) {
        if (!entry.formula) {
            continue;
        }
        const deps = formulaDependencies(entry.formula);
        if (!edges.has(entry.key)) {
            edges.set(entry.key, new Set());
        }
        for (const dep of deps) {
            edges.get(entry.key)?.add(dep);
        }
    }
    edges.set('is.gross_product_sales', new Set(GPS_IDENTITY));
    edges.set('is.total_orders', new Set(['is.avg_orders_per_day', 'is.days_open_for_business']));
    return edges;
}

function collectRefLineEdges(inputs: CaseInputs, view: View): Map<string, Set<string>> {
    const edges = new Map<string, Set<string>>();
    const viewDrivers = inputs.drivers[view] ?? {};
    for (const [lineKey, state] of Object.entries(viewDrivers)) {
        if (state.active !== 'pct_of_line' && state.active !== 'dollar_per_line_unit') {
            continue;
        }
        const ref = state.by_type[state.active]?.params?.ref;
        if (!ref) {
            continue;
        }
        if (!edges.has(lineKey)) {
            edges.set(lineKey, new Set());
        }
        edges.get(lineKey)?.add(ref);
    }
    return edges;
}

function canReach(
    edges: Map<string, Set<string>>,
    from: string,
    target: string,
    visited = new Set<string>(),
): boolean {
    if (from === target) {
        return true;
    }
    if (visited.has(from)) {
        return false;
    }
    visited.add(from);
    for (const next of edges.get(from) ?? []) {
        if (canReach(edges, next, target, visited)) {
            return true;
        }
    }
    return false;
}

export function wouldCreateCycle(
    schema: SchemaEntry[],
    inputs: CaseInputs,
    view: View,
    lineKey: string,
    refKey: string,
): boolean {
    const edges = collectFormulaEdges(schema);
    const refEdges = collectRefLineEdges(inputs, view);
    for (const [key, deps] of refEdges) {
        if (!edges.has(key)) {
            edges.set(key, new Set());
        }
        for (const dep of deps) {
            edges.get(key)?.add(dep);
        }
    }
    if (!edges.has(lineKey)) {
        edges.set(lineKey, new Set());
    }
    edges.get(lineKey)?.add(refKey);
    return canReach(edges, refKey, lineKey);
}

export function selectableRefs(
    schema: SchemaEntry[],
    inputs: CaseInputs,
    view: View,
    lineKey: string,
): string[] {
    const lineEntry = schema.find((entry) => entry.key === lineKey);
    const tab = lineEntry?.tab ?? 'IS';
    return schema
        .filter((entry) => {
            if (entry.tab !== tab) {
                return false;
            }
            if (entry.kind !== 'input' && entry.kind !== 'subtotal') {
                return false;
            }
            return true;
        })
        .map((entry) => entry.key)
        .filter(
            (refKey) =>
                refKey !== lineKey &&
                refKey !== 'is.boh' &&
                refKey !== 'is.foh' &&
                !wouldCreateCycle(schema, inputs, view, lineKey, refKey),
        )
        .sort();
}
